// src/pages/products/ProductUpdate.js
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Palette de couleurs SecureControl
const COLORS = {
  primary: '#0B3C5D',     // Bleu Sécurité
  secondary: '#1E88C8',   // Bleu Tech
  accent: '#4FC3F7',      // Bleu Clair
  darkText: '#2E2E2E',    // Gris Foncé
  lightBg: '#F5F7FA',     // Gris Clair
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3',
  successLight: '#e8f5e9',
  dangerLight: '#ffebee',
  warningLight: '#fff3e0',
  infoLight: '#e3f2fd'
};

const ProductUpdate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  
  // États du formulaire
  const [formData, setFormData] = useState({
    id_produit: "",
    libele_produit: "",
    id_type_produit: "",
    quantite_stock_produit: "",
    statut_produit: "1",
    seuil_alerte: ""
  });
  
  // États pour l'UI
  const [loading, setLoading] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [loadingProduct, setLoadingProduct] = useState(true);
  const [serverError, setServerError] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);
  const [hasPermission, setHasPermission] = useState(true);
  
  // États pour l'erreur d'abonnement
  const [abonnementError, setAbonnementError] = useState(false);
  const [abonnementMessage, setAbonnementMessage] = useState("");
  
  // États pour les types de produits
  const [typeProduits, setTypeProduits] = useState([]);
  const [filteredTypes, setFilteredTypes] = useState([]);
  const [typeSearch, setTypeSearch] = useState("");
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  
  // Fonction pour réinitialiser l'erreur d'abonnement
  const resetAbonnementError = () => {
    setAbonnementError(false);
    setAbonnementMessage("");
  };
  
  // Fonction pour charger les types de produits depuis l'API
  const loadTypeProduits = async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setServerError("Token d'authentification manquant.");
        setLoadingTypes(false);
        return;
      }
      
      const response = await axios.get(`${API_BASE_URL}/stock/type_produit/list_type_produit.php`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      console.log("Types produits chargés:", response.data);
      
      if (response.data?.success && response.data?.data) {
        const types = response.data.data.map(type => ({
          value: type.id.toString(),
          label: type.type
        }));
        
        setTypeProduits(types);
        setFilteredTypes(types);
      } else {
        setServerError("Erreur lors du chargement des types de produits.");
      }
    } catch (err) {
      console.error("Erreur chargement types produits:", err);
      
      // Vérifier si c'est une erreur 403 avec code ABONNEMENT_REQUIS
      if (err.response?.status === 403 && err.response?.data?.code === 'ABONNEMENT_REQUIS') {
        setAbonnementError(true);
        setAbonnementMessage(err.response.data.message || "Abonnement inactif");
        setTypeProduits([]);
        setFilteredTypes([]);
      } else if (err.response?.status === 401) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 403) {
        setServerError("Permission refusée pour accéder aux types de produits.");
      } else if (err.response?.status === 500) {
        setServerError("Erreur serveur lors du chargement des types de produits.");
      } else {
        setServerError("Erreur réseau lors du chargement des types de produits.");
      }
    } finally {
      setLoadingTypes(false);
    }
  };
  
  // Fonction pour charger les données du produit
  const loadProductData = async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setServerError("Token d'authentification manquant.");
        setLoadingProduct(false);
        return;
      }

      const response = await axios.get(
        `${API_BASE_URL}/stock/produits/list_produit.php`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.success) {
        const produits = response.data.data?.produits || [];
        const produit = produits.find(p => p.id == id || p.id_produit == id);
        
        if (produit) {
          const typeId = produit.id_type_produit?.toString() || "";
          const typeProduit = typeProduits.find(t => t.value === typeId);
          const typeLabel = typeProduit ? typeProduit.label : "";
          
          setFormData({
            id_produit: id,
            libele_produit: produit.libele || produit.libele_produit || "",
            id_type_produit: typeId,
            quantite_stock_produit: produit.quantite_stock?.toString() || produit.quantite_stock_produit?.toString() || "0",
            statut_produit: produit.statut?.toString() || produit.statut_produit?.toString() || "1",
            seuil_alerte: produit.seuil_alerte !== null && produit.seuil_alerte !== undefined ? produit.seuil_alerte.toString() : ""
          });
          
          setTypeSearch(typeLabel);
        } else {
          setServerError("Produit introuvable.");
        }
      } else {
        setServerError("Erreur lors du chargement des données du produit.");
      }
    } catch (err) {
      console.error("Erreur chargement produit:", err);
      
      if (err.response?.status === 401) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 403) {
        setServerError("Permission refusée pour accéder aux produits.");
      } else if (err.response?.status === 500) {
        setServerError("Erreur serveur lors du chargement du produit.");
      } else {
        setServerError("Erreur réseau lors du chargement du produit.");
      }
    } finally {
      setLoadingProduct(false);
    }
  };
  
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const userProfile = payload.profile;
        
        if (![1, 3].includes(userProfile)) {
          setHasPermission(false);
          setServerError("Permission refusée. Seuls les administrateurs et gestionnaires peuvent modifier un produit.");
        } else {
          const init = async () => {
            await loadTypeProduits();
            await loadProductData();
          };
          init();
        }
      } catch (err) {
        console.error("Erreur décodage token:", err);
        setLoadingTypes(false);
        setLoadingProduct(false);
      }
    } else {
      setServerError("Non authentifié. Veuillez vous connecter.");
      setLoadingTypes(false);
      setLoadingProduct(false);
    }
  }, [id]);
  
  // Effet pour filtrer les types quand la recherche change
  useEffect(() => {
    if (!typeSearch.trim()) {
      setFilteredTypes(typeProduits);
    } else {
      const searchTerm = typeSearch.toLowerCase();
      const filtered = typeProduits.filter(type => 
        type.label.toLowerCase().includes(searchTerm)
      );
      setFilteredTypes(filtered);
    }
  }, [typeSearch, typeProduits]);
  
  // Fermer la dropdown quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowTypeDropdown(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    let cleanedValue = value;
    
    if (name === "quantite_stock_produit") {
      cleanedValue = value.replace(/\D/g, "");
      const numValue = parseInt(cleanedValue);
      if (numValue > 999999) {
        cleanedValue = "999999";
      }
    }
    
    setFormData({
      ...formData,
      [name]: cleanedValue
    });
    
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };
  
  // Gérer la sélection d'un type
  const handleTypeSelect = (typeValue, typeLabel) => {
    setFormData({
      ...formData,
      id_type_produit: typeValue
    });
    setTypeSearch(typeLabel);
    setShowTypeDropdown(false);
  };
  
  // Gérer la recherche de type
  const handleTypeSearchChange = (e) => {
    const value = e.target.value;
    setTypeSearch(value);
    
    if (!value.trim()) {
      setFormData({
        ...formData,
        id_type_produit: ""
      });
    }
    
    if (value && !showTypeDropdown) {
      setShowTypeDropdown(true);
    }
  };
  
  const validateForm = () => {
    const errors = [];
    
    if (!formData.libele_produit.trim()) {
      errors.push("Le libellé du produit est obligatoire.");
    } else if (formData.libele_produit.trim().length < 2) {
      errors.push("Le libellé doit contenir au moins 2 caractères.");
    } else if (formData.libele_produit.trim().length > 100) {
      errors.push("Le libellé ne peut pas dépasser 100 caractères.");
    }
    
    if (!formData.id_type_produit || formData.id_type_produit === "") {
      errors.push("Le type de produit est obligatoire.");
    }
    
    const quantite = parseInt(formData.quantite_stock_produit);
    if (isNaN(quantite) || quantite < 0) {
      errors.push("La quantité ne peut pas être négative.");
    }
    
    if (!["0", "1"].includes(formData.statut_produit)) {
      errors.push("Le statut doit être 0 (inactif) ou 1 (actif).");
    }
    
    return errors;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Réinitialiser les messages
    setServerError("");
    setValidationErrors([]);
    resetAbonnementError();
    
    const errors = validateForm();
    if (errors.length > 0) {
      setValidationErrors(errors);
      setServerError("Veuillez corriger les erreurs dans le formulaire.");
      return;
    }
    
    const submitData = {
      id_produit: parseInt(id),
      libele_produit: formData.libele_produit.trim(),
      id_type_produit: parseInt(formData.id_type_produit),
      quantite_stock_produit: parseInt(formData.quantite_stock_produit),
      statut_produit: parseInt(formData.statut_produit),
      seuil_alerte: formData.seuil_alerte !== "" ? parseInt(formData.seuil_alerte) : null
    };
    
    setLoading(true);
    
    try {
      const url = `${API_BASE_URL}/stock/produits/update_produit.php`;
      console.log("Envoi des données pour modification:", submitData);
      
      const response = await axios.put(url, submitData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/json",
        },
      });
      
      console.log("Réponse modification produit:", response.data);
      
      if (response.data?.success) {
        setInfoMsg(response.data.message || "Produit modifié avec succès !");
        
        setTimeout(() => {
          navigate("/products");
        }, 2000);
      } else {
        setServerError(
          response.data?.message ||
          "Impossible de modifier le produit. Veuillez réessayer."
        );
        
        if (response.data?.errors) {
          setValidationErrors(response.data.errors);
        }
      }
    } catch (err) {
      console.error("Erreur modification produit:", err);
      
      // 🔍 Vérifier si c'est une erreur 403 avec code ABONNEMENT_REQUIS
      if (err.response?.status === 403 && err.response?.data?.code === 'ABONNEMENT_REQUIS') {
        setAbonnementError(true);
        setAbonnementMessage(err.response.data.message || "Abonnement inactif");
        setLoading(false);
        return;
      }
      
      if (err.response?.status === 400) {
        if (err.response.data?.errors) {
          setValidationErrors(err.response.data.errors);
          setServerError("Erreurs de validation");
        } else {
          setServerError(err.response.data?.message || "Données invalides.");
        }
      } else if (err.response?.status === 403) {
        setServerError(err.response.data?.message || "Permission refusée. Seuls les administrateurs et gestionnaires peuvent modifier un produit.");
      } else if (err.response?.status === 404) {
        setServerError("Produit ou type de produit introuvable.");
      } else if (err.response?.status === 401) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 500) {
        setServerError("Erreur serveur. Veuillez réessayer plus tard.");
      } else {
        setServerError("Erreur réseau. Vérifiez votre connexion.");
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancel = () => {
    if (window.confirm("Annuler les modifications ? Les changements seront perdus.")) {
      navigate("/products");
    }
  };
  
  // Obtenir le type sélectionné
  const selectedType = typeProduits.find(t => t.value === formData.id_type_produit);
  
  // Si l'utilisateur n'a pas la permission
  if (!hasPermission) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion de stocks</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</a>
              </li>
              <li className="breadcrumb-item">
                <a href="/products" style={{ color: COLORS.primary }}>Produits</a>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>
                Modifier produit #{id}
              </li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="alert alert-dismissible fade show" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.danger}`,
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-shield-exclamation me-2 fs-4"></i>
                  <div className="flex-grow-1">{serverError}</div>
                </div>
              </div>
              <button
                className="btn fw-semibold mt-4"
                onClick={() => navigate("/products")}
                style={{
                  color: COLORS.white,
                  backgroundColor: COLORS.primary,
                  borderColor: COLORS.primary,
                  borderRadius: '8px',
                  padding: '10px 24px',
                  minWidth: '200px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#0A3250';
                  e.target.style.borderColor = '#0A3250';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = COLORS.primary;
                  e.target.style.borderColor = COLORS.primary;
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Retour à la liste
              </button>
            </div>
          </div>
        </section>
      </Layout>
    );
  }
  
  // État de chargement initial
  if (loadingProduct || loadingTypes) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion de stocks</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</a>
              </li>
              <li className="breadcrumb-item">
                <a href="/products" style={{ color: COLORS.primary }}>Produits</a>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>
                Modifier produit #{id}
              </li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                <span className="visually-hidden">Chargement...</span>
              </div>
              <p className="mt-3" style={{ color: COLORS.darkText }}>
                {loadingProduct ? "Chargement des données du produit..." : 
                 loadingTypes ? "Chargement des types de produits..." : "Chargement..."}
              </p>
            </div>
          </div>
        </section>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion de stocks</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</a>
            </li>
            <li className="breadcrumb-item">
              <a href="/products" style={{ color: COLORS.primary }}>Produits</a>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>
              Modifier produit
            </li>
          </ol>
        </nav>
      </div>
      
      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h5 className="card-title mb-1" style={{ 
                      color: COLORS.primary,
                      paddingBottom: '16px',
                      width: '100%'
                    }}>
                      <div className="d-flex align-items-center">
                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                          width: '36px',
                          height: '36px',
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary
                        }}>
                           <i className="bi bi-pencil-square"></i>
                        </div>
                        Modifier le produit
                      </div>
                    </h5>
                  </div>
             </div>
          </div>

            {/* Affichage de l'erreur d'abonnement */}
            {abonnementError && (
              <div className="alert alert-warning alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#fff3e0',
                borderColor: COLORS.warning,
                color: '#ff8f00',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.warning}`,
                whiteSpace: "pre-line"
              }}>
                <div className="d-flex align-items-start">
                  <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
                  <div className="flex-grow-1">
                    {abonnementMessage}
                    <div className="d-flex gap-2 justify-content-center mt-3">
                      <a 
                        href="/abonnements/create"
                        className="btn fw-semibold"
                        style={{
                          backgroundColor: '#4caf50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 20px',
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
                      >
                        <i className="bi bi-box-arrow-up-right"></i>
                        Souscrire à un abonnement
                      </a>
                      <button
                        className="btn fw-semibold"
                        onClick={() => {
                          setAbonnementError(false);
                          setAbonnementMessage("");
                        }}
                        style={{
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 20px'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
                      >
                        Fermer
                      </button>
                    </div>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => {
                      setAbonnementError(false);
                      setAbonnementMessage("");
                    }}
                  ></button>
                </div>
              </div>
            )}
            
            {/* Messages d'erreur/succès */}
            {serverError && !abonnementError && !validationErrors.length && (
              <div className="alert alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.danger}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{serverError}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setServerError("")}
                  ></button>
                </div>
              </div>
            )}
            
            {validationErrors.length > 0 && (
              <div className="alert alert-warning alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#fff3e0',
                borderColor: COLORS.warning,
                color: '#f57c00',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.warning}`
              }}>
                <div className="d-flex align-items-start">
                  <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
                  <div className="flex-grow-1">
                    <h6 className="alert-heading mb-2" style={{ color: '#e65100' }}>
                      Erreurs de validation
                    </h6>
                    <ul className="mb-0 ps-3" style={{ fontSize: '0.9rem' }}>
                      {validationErrors.map((error, index) => (
                        <li key={index} style={{ marginBottom: '4px' }}>{error}</li>
                      ))}
                    </ul>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setValidationErrors([])}
                  ></button>
                </div>
              </div>
            )}
            
            {infoMsg && (
              <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#e8f5e9',
                borderColor: COLORS.success,
                color: '#2e7d32',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.success}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-check-circle me-2"></i>
                  <div className="flex-grow-1">
                    {infoMsg}
                    <div className="mt-2 small" style={{ color: '#1b5e20' }}>
                      <div className="spinner-border spinner-border-sm text-success me-2" role="status">
                        <span className="visually-hidden">Chargement...</span>
                      </div>
                      Redirection vers la liste des produits...
                    </div>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setInfoMsg("")}></button>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* Libellé du produit */}
                <div className="col-12 mb-4">
                  <label htmlFor="libele_produit" className="form-label fw-bold mb-3" style={{ 
                    color: COLORS.darkText,
                    fontSize: '0.95rem'
                  }}>
                    <span className="text-danger">*</span> Libellé du produit :
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="libele_produit"
                    name="libele_produit"
                    value={formData.libele_produit}
                    onChange={handleChange}
                    placeholder="Ex: Ordinateur portable, T-shirt, etc."
                    maxLength="100"
                    disabled={loading}
                    required
                    autoFocus
                    style={{
                      borderColor: '#ced4da',
                      borderRadius: '6px',
                      padding: '10px 12px',
                      fontSize: '0.95rem',
                      backgroundColor: COLORS.white,
                      transition: 'all 0.3s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = COLORS.secondary;
                      e.target.style.boxShadow = `0 0 0 0.2rem rgba(30, 136, 200, 0.1)`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#ced4da';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  <div className="form-text mt-2" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                    <i className="bi bi-info-circle me-1"></i>
                    Nom du produit (entre 2 et 100 caractères)
                  </div>
                </div>
                
                {/* Type de produit avec recherche */}
                <div className="col-12 mb-4" ref={dropdownRef}>
                  <label className="form-label fw-bold mb-3" style={{ 
                    color: COLORS.darkText,
                    fontSize: '0.95rem'
                  }}>
                    <span className="text-danger">*</span> Type de produit :
                  </label>
                  
                  <div className="position-relative">
                    <div className="input-group">
                      <span className="input-group-text" style={{
                        backgroundColor: COLORS.lightBg,
                        borderColor: '#e0e0e0',
                        color: COLORS.secondary
                      }}>
                        <i className="bi bi-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        id="type-search"
                        placeholder="Rechercher un type de produit..."
                        value={typeSearch}
                        onChange={handleTypeSearchChange}
                        onFocus={() => setShowTypeDropdown(true)}
                        disabled={loading || loadingTypes}
                        required
                        style={{
                          borderColor: '#ced4da',
                          padding: '10px 12px',
                          fontSize: '0.95rem',
                          backgroundColor: COLORS.white,
                          transition: 'all 0.3s ease'
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#ced4da';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                      {formData.id_type_produit && (
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={() => {
                            setFormData({ ...formData, id_type_produit: "" });
                            setTypeSearch("");
                          }}
                          title="Effacer la sélection"
                          disabled={loading || loadingTypes}
                        >
                          <i className="bi bi-x"></i>
                        </button>
                      )}
                    </div>
                    
                    {showTypeDropdown && !loadingTypes && typeProduits.length > 0 && (
                      <div className="dropdown-menu show w-100" style={{ 
                        maxHeight: "250px", 
                        overflowY: "auto",
                        zIndex: 1050,
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        border: 'none'
                      }}>
                        {filteredTypes.length > 0 ? (
                          filteredTypes.map((type) => (
                            <button
                              key={type.value}
                              type="button"
                              className={`dropdown-item d-flex justify-content-between align-items-center ${formData.id_type_produit === type.value ? 'bg-primary text-white' : ''}`}
                              onClick={() => handleTypeSelect(type.value, type.label)}
                              style={{
                                padding: '10px 16px'
                              }}
                            >
                              <div>
                                <div className="fw-bold" style={{ fontSize: "0.95rem" }}>{type.label}</div>
                              </div>
                              {formData.id_type_produit === type.value && (
                                <i className="bi bi-check-lg"></i>
                              )}
                            </button>
                          ))
                        ) : (
                          <div className="dropdown-item text-muted text-center py-3">
                            <i className="bi bi-search me-2"></i>
                            Aucun type trouvé
                          </div>
                        )}
                      </div>
                    )}
                    
                    {formData.id_type_produit && typeProduits.length > 0 && (
                      <div className="mt-3">
                        <span className="badge fw-semibold" style={{ 
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary,
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '0.85rem'
                        }}>
                          <i className="bi bi-check-circle me-1"></i>
                          Sélectionné: {typeProduits.find(t => t.value === formData.id_type_produit)?.label}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {loadingTypes && (
                    <div className="form-text mt-2 d-flex align-items-center" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                      <div className="spinner-border spinner-border-sm me-2" role="status" style={{ color: COLORS.primary }}>
                        <span className="visually-hidden">Chargement...</span>
                      </div>
                      Chargement des types de produits...
                    </div>
                  )}
                  
                  {typeProduits.length === 0 && !loadingTypes && !abonnementError && (
                    <div className="alert alert-warning alert-dismissible fade show mt-3" style={{ 
                      backgroundColor: '#fff3e0',
                      borderColor: COLORS.warning,
                      color: '#f57c00',
                      borderRadius: '8px',
                      borderLeft: `4px solid ${COLORS.warning}`,
                      padding: '12px 16px'
                    }}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-exclamation-triangle me-2 fs-5"></i>
                        <div className="flex-grow-1">
                          Aucun type de produit disponible.
                        </div>
                        <button 
                          type="button" 
                          className="btn btn-sm btn-outline-primary ms-2"
                          onClick={() => navigate("/product-types")}
                          style={{
                            color: COLORS.primary,
                            borderColor: COLORS.primary,
                            borderRadius: '6px',
                            padding: '4px 12px',
                            fontSize: '0.85rem'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#E3F2FD';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                          }}
                        >
                          <i className="bi bi-plus me-1"></i> Créer un type
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {typeProduits.length > 0 && !loadingTypes && (
                    <div className="form-text mt-2 d-flex align-items-center" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                      <i className="bi bi-info-circle me-2"></i>
                      <span>
                        {typeProduits.length} type{typeProduits.length > 1 ? 's' : ''} disponible{typeProduits.length > 1 ? 's' : ''}
                        {typeSearch && filteredTypes.length > 0 && (
                          <span className="ms-2">
                            ({filteredTypes.length} résultat{filteredTypes.length > 1 ? 's' : ''})
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Quantité en stock */}
                <div className="col-md-6 mb-4">
                  <label htmlFor="quantite_stock_produit" className="form-label fw-bold mb-3" style={{ 
                    color: COLORS.darkText,
                    fontSize: '0.95rem'
                  }}>
                    Quantité en stock :
                  </label>
                  <div className="input-group">
                    <span className="input-group-text" style={{
                      backgroundColor: COLORS.lightBg,
                      borderColor: '#e0e0e0',
                      color: COLORS.secondary
                    }}>
                      <i className="bi bi-box"></i>
                    </span>
                    <input
                      type="number"
                      className="form-control"
                      id="quantite_stock_produit"
                      name="quantite_stock_produit"
                      value={formData.quantite_stock_produit}
                      onChange={handleChange}
                      min="0"
                      max="999999"
                      disabled={loading}
                      style={{
                        borderColor: '#ced4da',
                        borderRadius: '6px',
                        padding: '10px 12px',
                        fontSize: '0.95rem',
                        backgroundColor: COLORS.white
                      }}
                    />
                  </div>
                  <div className="form-text mt-2" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                    <i className="bi bi-info-circle me-1"></i>
                    Quantité actuelle en stock (0 à 999999)
                  </div>
                </div>

                {/* Seuil d'alerte de stock faible */}
                <div className="col-md-6 mb-4">
                  <label htmlFor="seuil_alerte" className="form-label fw-bold mb-3" style={{
                    color: COLORS.darkText,
                    fontSize: '0.95rem'
                  }}>
                    Seuil d'alerte (optionnel) :
                  </label>
                  <div className="input-group">
                    <span className="input-group-text" style={{
                      backgroundColor: COLORS.lightBg,
                      borderColor: '#e0e0e0',
                      color: COLORS.warning || '#ff9800'
                    }}>
                      <i className="bi bi-exclamation-triangle"></i>
                    </span>
                    <input
                      type="number"
                      className="form-control"
                      id="seuil_alerte"
                      name="seuil_alerte"
                      value={formData.seuil_alerte}
                      onChange={handleChange}
                      min="0"
                      disabled={loading}
                      style={{
                        borderColor: '#ced4da',
                        borderRadius: '6px',
                        padding: '10px 12px',
                        fontSize: '0.95rem',
                        backgroundColor: COLORS.white
                      }}
                    />
                  </div>
                  <div className="form-text mt-2" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                    <i className="bi bi-info-circle me-1"></i>
                    Alerte affichée dès que le stock descend à ce niveau. Laissez vide pour désactiver.
                  </div>
                </div>

                {/* Statut du produit */}
                <div className="col-md-6 mb-4">
                  <label className="form-label fw-bold mb-3" style={{ 
                    color: COLORS.darkText,
                    fontSize: '0.95rem'
                  }}>
                    Statut du produit :
                  </label>
                  <div className="d-flex gap-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="statut_produit"
                        id="statut_actif"
                        value="1"
                        checked={formData.statut_produit === "1"}
                        onChange={handleChange}
                        disabled={loading}
                        style={{
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer'
                        }}
                      />
                      <label className="form-check-label ms-2 fw-semibold" htmlFor="statut_actif" style={{ 
                        color: COLORS.darkText,
                        cursor: 'pointer'
                      }}>
                        <span className="badge bg-success me-2">Actif</span>
                        Disponible et visible
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="statut_produit"
                        id="statut_inactif"
                        value="0"
                        checked={formData.statut_produit === "0"}
                        onChange={handleChange}
                        disabled={loading}
                        style={{
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer'
                        }}
                      />
                      <label className="form-check-label ms-2 fw-semibold" htmlFor="statut_inactif" style={{ 
                        color: COLORS.darkText,
                        cursor: 'pointer'
                      }}>
                        <span className="badge bg-secondary me-2">Inactif</span>
                        Désactivé
                      </label>
                    </div>
                  </div>
                  <div className="form-text mt-2" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                    <i className="bi bi-info-circle me-1"></i>
                    Un produit inactif ne sera pas visible dans les listes
                  </div>
                </div>
              </div>
              
              <div className="mt-5 pt-4 border-top" style={{ borderColor: '#e0e0e0' }}>
                <div className="d-flex justify-content-end gap-3">
                  <button
                    type="button"
                    className="btn fw-semibold"
                    onClick={handleCancel}
                    disabled={loading || loadingTypes}
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '10px 24px',
                      minWidth: '120px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!loading && !loadingTypes) {
                        e.target.style.backgroundColor = '#E3F2FD';
                        e.target.style.borderColor = COLORS.secondary;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loading && !loadingTypes) {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.borderColor = COLORS.primary;
                      }
                    }}
                  >
                    <i className="bi bi-x-lg me-2"></i>
                    Annuler
                  </button>
                  
                  <button
                    type="submit"
                    className="btn fw-semibold"
                    disabled={loading || loadingTypes || typeProduits.length === 0 || !formData.id_type_produit}
                    style={{
                      color: COLORS.white,
                      backgroundColor: COLORS.primary,
                      borderColor: COLORS.primary,
                      borderRadius: '8px',
                      padding: '10px 24px',
                      minWidth: '180px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!loading && !loadingTypes && typeProduits.length > 0 && formData.id_type_produit) {
                        e.target.style.backgroundColor = '#0A3250';
                        e.target.style.borderColor = '#0A3250';
                        e.target.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loading && !loadingTypes && typeProduits.length > 0 && formData.id_type_produit) {
                        e.target.style.backgroundColor = COLORS.primary;
                        e.target.style.borderColor = COLORS.primary;
                        e.target.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Modification en cours...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-lg me-2 fw-bold"></i>
                        Modifier le produit
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .btn:focus,
        .form-control:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .form-control.is-invalid {
          border-color: ${COLORS.danger};
          background-color: #fff5f5;
        }
        
        .form-control.is-invalid:focus {
          box-shadow: 0 0 0 0.2rem rgba(244, 67, 54, 0.25);
        }
        
        .breadcrumb-item a {
          text-decoration: none;
          transition: color 0.3s ease;
        }
        
        .breadcrumb-item a:hover {
          color: ${COLORS.secondary} !important;
        }
        
        .dropdown-item.active, 
        .dropdown-item:active {
          background-color: ${COLORS.primary} !important;
        }
        
        .form-check-input:checked {
          background-color: ${COLORS.primary};
          border-color: ${COLORS.primary};
        }
        
        .badge.bg-success {
          background-color: ${COLORS.success} !important;
        }
        
        .badge.bg-secondary {
          background-color: #6c757d !important;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-end {
            flex-direction: column;
            gap: 12px;
          }
          
          .btn {
            width: 100%;
            justify-content: center;
          }
          
          .col-md-6 {
            margin-bottom: 1rem;
          }
          
          .d-flex.gap-4 {
            gap: 2rem !important;
          }
        }
      `}</style>
    </Layout>
  );
};

export default ProductUpdate;